/* ==========================================================================
   ZEN - 然 - EYEWEAR — zen.js
   Small, dependency-free. No scroll listeners except the hero fallback,
   which only runs while the hero is on screen and the browser lacks
   CSS scroll-driven animations.
   ========================================================================== */

(() => {
  if (window.ZenTheme) return;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionAllowed = () => !reduceMotion.matches && !document.body.classList.contains('zen-motion-off');

  /* ------------------------------------------------------------------
     Reveal on view
     ------------------------------------------------------------------ */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  function initReveal(scope) {
    scope.querySelectorAll('[data-zen-reveal]:not(.is-revealed)').forEach((el) => {
      if (!revealObserver || !motionAllowed()) {
        el.classList.add('is-revealed');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ------------------------------------------------------------------
     Smoke: pause drifting layers while off-screen
     ------------------------------------------------------------------ */
  const smokeObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => entry.target.classList.toggle('is-paused', !entry.isIntersecting));
        })
      : null;

  function initSmoke(scope) {
    if (!smokeObserver) return;
    scope.querySelectorAll('[data-zen-smoke]').forEach((el) => smokeObserver.observe(el));
  }

  /* ------------------------------------------------------------------
     Hero scroll progress fallback
     Browsers with scroll-driven animations animate --zen-hero-p in CSS.
     Others get a rAF-throttled scroll handler active only while the hero
     is visible.
     ------------------------------------------------------------------ */
  const supportsScrollTimeline = window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()');

  function initHero(scope) {
    if (supportsScrollTimeline || !motionAllowed()) return;
    scope.querySelectorAll('[data-zen-hero]').forEach((hero) => {
      if (hero.dataset.zenHeroInit) return;
      hero.dataset.zenHeroInit = 'true';

      let active = false;
      let ticking = false;
      const update = () => {
        ticking = false;
        const range = window.innerHeight * 0.7;
        const p = Math.min(Math.max(window.scrollY / range, 0), 1);
        hero.style.setProperty('--zen-hero-p', p.toFixed(3));
      };
      const onScroll = () => {
        if (!active || ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };

      new IntersectionObserver((entries) => {
        active = entries[0].isIntersecting;
        if (active) update();
      }).observe(hero);

      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ------------------------------------------------------------------
     Header — transparent → solid, full-screen mobile menu
     ------------------------------------------------------------------ */
  class ZenHeader extends HTMLElement {
    connectedCallback() {
      this.sentinel = document.getElementById('zen-scroll-sentinel');
      this.menu = document.getElementById(this.dataset.menuId);
      this.openButton = this.querySelector('[data-zen-menu-open]');

      // Overlay mode only makes sense when a hero sits underneath.
      const hero = document.querySelector('[data-zen-hero]');
      if (this.classList.contains('zen-header--overlay')) {
        if (!hero) {
          this.classList.remove('zen-header--overlay');
        } else {
          // Light photo directly under the transparent header → dark ink
          this.classList.toggle('zen-header--ink-d', hero.dataset.headerD === 'ink');
          this.classList.toggle('zen-header--ink-m', hero.dataset.headerM === 'ink');
        }
      }

      if (this.sentinel && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-scrolled', !entry.isIntersecting);
        });
        this.observer.observe(this.sentinel);
      }

      if (this.menu && this.openButton) this.initMenu();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    initMenu() {
      this.closeButton = this.menu.querySelector('[data-zen-menu-close]');

      this.openButton.addEventListener('click', () => this.openMenu());
      if (this.closeButton) this.closeButton.addEventListener('click', () => this.closeMenu());

      // ESC → animated close instead of the dialog's instant close
      this.menu.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeMenu();
      });

      // Same-page anchor links: close first so the page can scroll.
      this.menu.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => this.closeMenu(true));
      });

      // Closing the menu when the viewport grows to desktop
      window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
        if (e.matches && this.menu.open) this.closeMenu(true);
      });
    }

    openMenu() {
      if (this.menu.open) return;
      this.menu.showModal();
      root.classList.add('zen-lock');
      this.openButton.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.menu.classList.add('is-open'));
    }

    closeMenu(immediate = false) {
      if (!this.menu.open) return;
      const finish = () => {
        this.menu.close();
        this.menu.classList.remove('is-closing');
        root.classList.remove('zen-lock');
        this.openButton.setAttribute('aria-expanded', 'false');
        if (!immediate) this.openButton.focus({ preventScroll: true });
      };
      this.menu.classList.remove('is-open');
      if (immediate || !motionAllowed()) {
        finish();
        return;
      }
      this.menu.classList.add('is-closing');
      setTimeout(finish, 450);
    }
  }

  if (!customElements.get('zen-header')) customElements.define('zen-header', ZenHeader);

  /* ------------------------------------------------------------------
     Boot + Theme Editor support
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     "Clear" moments (Anti-Fog): fog thins once the section is in view
     ------------------------------------------------------------------ */
  const clearObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-clear');
              clearObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.45 }
        )
      : null;

  function initClear(scope) {
    scope.querySelectorAll('[data-zen-clear]:not(.is-clear)').forEach((el) => {
      if (!clearObserver || !motionAllowed()) el.classList.add('is-clear');
      else clearObserver.observe(el);
    });
  }

  function init(scope = document) {
    initReveal(scope);
    initClear(scope);
    initSmoke(scope);
    initHero(scope);
  }

  window.ZenTheme = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
